
import React, { useState, useEffect, useMemo } from 'react';
import { adminWithdrawalService, AdminWithdrawalEntry, AdminWithdrawalDetail } from '../../services/admin.withdrawal.service';
import Button from '../../components/common/Button';
import Input from '../../components/common/Input';
import {
  Banknote,
  Search,
  CheckCircle2,
  XCircle,
  Clock,
  Eye,
  X,
  User,
  Wallet,
  Calendar,
  ShieldCheck,
  ChevronRight,
  ChevronLeft,
  Info,
  ShieldAlert,
  Loader2,
  FileText
} from 'lucide-react';
import { useToast } from '../../context/ToastContext';

const AdminWithdrawals: React.FC = () => {
  const { showToast } = useToast();
  const [requests, setRequests] = useState<AdminWithdrawalEntry[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);

  const [selectedWithdrawalId, setSelectedWithdrawalId] = useState<string | null>(null);
  const [withdrawalDetail, setWithdrawalDetail] = useState<AdminWithdrawalDetail | null>(null);
  const [isDetailLoading, setIsDetailLoading] = useState(false);

  const [isProcessing, setIsProcessing] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [showRejectForm, setShowRejectForm] = useState(false);

  const limit = 10;

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const response = await adminWithdrawalService.listAllWithdrawals({
        page,
        limit,
        search: searchQuery || undefined
      });
      if (response.success) {
        // Status Ordering: PENDING -> APPROVED -> REJECTED
        const sorted = (response.data || []).sort((a, b) => {
          const order = { 'PENDING': 1, 'APPROVED': 2, 'REJECTED': 3 };
          if (order[a.status] !== order[b.status]) {
            return order[a.status] - order[b.status];
          }
          return new Date(b.requestedAt).getTime() - new Date(a.requestedAt).getTime();
        });
        setRequests(sorted);
      }
    } catch (error) {
      showToast('Vault Sync Failure: Treasury engine disconnected', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchDetail = async (id: string) => {
    setIsDetailLoading(true);
    try {
      const response = await adminWithdrawalService.getWithdrawalDetail(id);
      if (response.success) {
        setWithdrawalDetail(response.data);
      }
    } catch (error) {
      showToast('Detail Retrieval Error: Protocol violation', 'error');
    } finally {
      setIsDetailLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [page, searchQuery]);

  useEffect(() => {
    if (selectedWithdrawalId) {
      fetchDetail(selectedWithdrawalId);
    } else {
      setWithdrawalDetail(null);
    }
  }, [selectedWithdrawalId]);

  const handleAction = async (action: 'approve' | 'reject') => {
    if (!selectedWithdrawalId || !withdrawalDetail) return;
    if (action === 'reject' && !rejectionReason) {
      setShowRejectForm(true);
      return;
    }

    setIsProcessing(true);
    try {
      const response = action === 'approve'
        ? await adminWithdrawalService.approveWithdrawal(selectedWithdrawalId)
        : await adminWithdrawalService.rejectWithdrawal(selectedWithdrawalId, rejectionReason);

      if (response.success) {
        showToast(`Treasury Success: Withdrawal ${action === 'approve' ? 'Authorized' : 'Purged'}`, 'success');
        setSelectedWithdrawalId(null);
        setShowRejectForm(false);
        setRejectionReason('');
        fetchData();
      } else {
        showToast('Execution Rejected: Protocol Conflict', 'error');
      }
    } catch (error) {
      showToast('Terminal Error: Connection to settlement engine lost', 'error');
    } finally {
      setIsProcessing(false);
    }
  };

  const statusStyles = {
    'APPROVED': 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20',
    'REJECTED': 'bg-red-500/10 text-red-500 border-red-500/20',
    'PENDING': 'bg-brand-primary/10 text-brand-primary border-brand-primary/20'
  };

  const maskAddress = (addr: string) => addr ? `${addr.slice(0, 8)}...${addr.slice(-6)}` : 'N/A';

  return (
    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-700 pb-24">

      {/* 1. Module Header */}
      <section className="flex flex-col md:flex-row md:items-center justify-between gap-6">
        <div>
          <h1 className="text-3xl font-bold text-white tracking-tight uppercase italic text-brand-primary">Treasury Control</h1>
          <p className="text-brand-muted text-xs font-bold uppercase tracking-widest opacity-70">Global Liquidity Outflow & Payout Verification</p>
        </div>

        <div className="w-full md:w-96 relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-brand-muted" size={18} />
          <input
            type="text"
            placeholder="Search Entity Name or Protocol Hash..."
            value={searchQuery}
            onChange={(e) => { setSearchQuery(e.target.value); setPage(1); }}
            className="w-full bg-brand-card border border-brand-border rounded-xl pl-12 pr-4 py-3 text-xs text-white focus:outline-none focus:border-brand-primary transition-all uppercase tracking-widest"
          />
        </div>
      </section>

      {/* 2. Transaction Ledger */}
      <div className="min-h-[400px]">
        {/* Desktop View */}
        <div className="hidden md:block bg-brand-card border border-brand-border rounded-3xl overflow-hidden shadow-2xl relative">
          {isLoading && (
            <div className="absolute inset-0 bg-brand-dark/20 backdrop-blur-[1px] z-10 flex items-center justify-center">
              <Loader2 className="animate-spin text-brand-primary" size={32} />
            </div>
          )}
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-brand-dark/40 border-b border-brand-border">
                <th className="px-6 py-5 text-[10px] font-black text-brand-muted uppercase tracking-[0.2em]">Hash / UID</th>
                <th className="px-6 py-5 text-[10px] font-black text-brand-muted uppercase tracking-[0.2em]">Entity Intelligence</th>
                <th className="px-6 py-5 text-[10px] font-black text-brand-muted uppercase tracking-[0.2em]">Redemption</th>
                <th className="px-6 py-5 text-[10px] font-black text-brand-muted uppercase tracking-[0.2em]">Protocol</th>
                <th className="px-6 py-5 text-[10px] font-black text-brand-muted uppercase tracking-[0.2em]">Current State</th>
                <th className="px-6 py-5 text-[10px] font-black text-brand-muted uppercase tracking-[0.2em] text-right">Audit</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-brand-border">
              {requests.length === 0 && !isLoading ? (
                <tr>
                  <td colSpan={6} className="px-6 py-20 text-center text-brand-muted text-[10px] font-black uppercase tracking-widest opacity-40">
                    No liquidity movement detected in the nexus
                  </td>
                </tr>
              ) : requests.map((req) => (
                <tr key={req.id} className="hover:bg-brand-primary/5 transition-colors group">
                  <td className="px-6 py-5 font-mono text-[10px] text-brand-muted group-hover:text-brand-primary transition-colors">
                    {req.id.slice(0, 12)}...
                    <p className="text-[8px] opacity-40 mt-1 uppercase font-bold">{req.id}</p>
                  </td>
                  <td className="px-6 py-5">
                    <div>
                      <p className="text-sm font-bold text-white leading-none uppercase italic">{req.userName || 'Unknown Entity'}</p>
                      <p className="text-[9px] text-brand-muted mt-2 uppercase tracking-widest font-black opacity-60">ID: {req.userId.slice(0, 13)}</p>
                    </div>
                  </td>
                  <td className="px-6 py-5">
                    <span className="text-sm font-black text-white tracking-tighter font-mono">
                      ${parseFloat(req.amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                    </span>
                    <p className="text-[9px] text-brand-primary font-black mt-1">{req.asset}</p>
                  </td>
                  <td className="px-6 py-5">
                    <span className="text-[10px] font-mono text-brand-muted opacity-70 bg-brand-dark px-2 py-1 rounded border border-brand-border" title={req.destinationAddress}>
                      {maskAddress(req.destinationAddress)}
                    </span>
                  </td>
                  <td className="px-6 py-5">
                    <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-md text-[9px] font-black uppercase border tracking-widest ${statusStyles[req.status]}`}>
                      {req.status === 'PENDING' && <Clock size={12} />}
                      {req.status === 'APPROVED' && <CheckCircle2 size={12} />}
                      {req.status === 'REJECTED' && <XCircle size={12} />}
                      {req.status}
                    </div>
                  </td>
                  <td className="px-6 py-5 text-right">
                    <button
                      onClick={() => setSelectedWithdrawalId(req.id)}
                      className="p-2.5 rounded-xl bg-brand-dark border border-brand-border text-brand-muted hover:text-white hover:border-brand-primary/30 transition-all shadow-xl"
                    >
                      <Eye size={18} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Pagination Desktop */}
          {requests.length > 0 && (
            <div className="p-4 bg-brand-dark/20 border-t border-brand-border flex items-center justify-between">
              <span className="text-[10px] text-brand-muted font-black uppercase tracking-widest opacity-60 px-2 italic">Sequence Page {page}</span>
              <div className="flex items-center gap-4">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1 || isLoading}
                  className="p-2 rounded-xl bg-brand-dark border border-brand-border text-brand-muted hover:text-white disabled:opacity-30 transition-all"
                >
                  <ChevronLeft size={18} />
                </button>
                <button
                  onClick={() => setPage(p => p + 1)}
                  disabled={requests.length < limit || isLoading}
                  className="p-2 rounded-xl bg-brand-dark border border-brand-border text-brand-muted hover:text-white disabled:opacity-30 transition-all"
                >
                  <ChevronRight size={18} />
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Mobile View */}
        <div className="md:hidden space-y-4 relative">
          {isLoading && (
            <div className="absolute inset-0 bg-brand-dark/20 backdrop-blur-[1px] z-10 flex items-center justify-center">
              <Loader2 className="animate-spin text-brand-primary" size={32} />
            </div>
          )}
          {requests.map((req) => (
            <div key={req.id} className="bg-brand-card border border-brand-border rounded-3xl p-6 space-y-4 shadow-2xl active:scale-[0.98] transition-transform">
              <div className="flex justify-between items-start">
                <div className="space-y-1">
                  <span className="text-[9px] text-brand-muted font-black uppercase tracking-widest opacity-60">{req.id.slice(0, 12)}...</span>
                  <h4 className="font-bold text-white uppercase italic">{req.userName}</h4>
                </div>
                <div className={`px-2 py-1 rounded-md text-[9px] font-black uppercase border tracking-widest ${statusStyles[req.status]}`}>
                  {req.status}
                </div>
              </div>
              <div className="flex items-center justify-between text-[11px] text-brand-muted py-4 border-y border-brand-border/30">
                <div className="flex flex-col">
                  <p className="text-[8px] font-black uppercase tracking-widest mb-1 opacity-50">Settlement</p>
                  <span className="font-black text-white font-mono text-base">${parseFloat(req.amount).toLocaleString()}</span>
                </div>
                <div className="text-right flex flex-col items-end">
                  <p className="text-[8px] font-black uppercase tracking-widest mb-1 opacity-50">Protocol</p>
                  <span className="font-mono text-brand-primary">{maskAddress(req.destinationAddress)}</span>
                </div>
              </div>
              <button
                onClick={() => setSelectedWithdrawalId(req.id)}
                className="w-full text-brand-primary font-black uppercase tracking-[0.3em] text-[10px] flex items-center justify-center gap-3 py-2 bg-brand-primary/5 rounded-xl border border-brand-primary/10"
              >
                OPEN AUDIT TERMINAL <ChevronRight size={14} />
              </button>
            </div>
          ))}

          {/* Mobile Pagination */}
          {requests.length > 0 && (
            <div className="flex items-center justify-between px-2 pt-4">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1 || isLoading}
                className="p-3 rounded-2xl bg-brand-card border border-brand-border text-brand-muted active:scale-95 transition-all"
              >
                <ChevronLeft size={20} />
              </button>
              <span className="text-[10px] text-brand-muted font-black uppercase tracking-widest italic">Page {page}</span>
              <button
                onClick={() => setPage(p => p + 1)}
                disabled={requests.length < limit || isLoading}
                className="p-3 rounded-2xl bg-brand-card border border-brand-border text-brand-muted active:scale-95 transition-all"
              >
                <ChevronRight size={20} />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* 3. Inspection Terminal (Audit Modal) */}
      {selectedWithdrawalId && (
        <div className="fixed inset-0 z-[150] flex items-center justify-center p-0 md:p-6 bg-brand-dark/95 backdrop-blur-md animate-in fade-in duration-300">
          <div className="bg-brand-card border border-brand-border/50 w-full max-w-4xl h-full md:h-auto md:max-h-[95vh] md:rounded-[40px] overflow-hidden shadow-[0_0_150px_rgba(0,0,0,1)] flex flex-col animate-in zoom-in-95 duration-500">

            {/* Terminal Header */}
            <div className="p-8 border-b border-brand-border flex items-center justify-between bg-brand-dark/40 shrink-0">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-2xl bg-brand-primary/10 border border-brand-primary/20 flex items-center justify-center text-brand-primary shadow-inner">
                  <Banknote size={32} />
                </div>
                <div>
                  <h3 className="text-2xl font-black text-white uppercase italic tracking-tighter">Settlement Audit Path</h3>
                  <div className="flex items-center gap-3 mt-1">
                    <span className="text-[10px] text-brand-muted uppercase font-black tracking-widest">Protocol ID:</span>
                    <code className="text-[10px] font-mono text-brand-primary">{selectedWithdrawalId}</code>
                  </div>
                </div>
              </div>
              <button
                onClick={() => {
                  setSelectedWithdrawalId(null);
                  setShowRejectForm(false);
                }}
                className="p-4 rounded-2xl bg-brand-dark hover:bg-brand-border text-brand-muted hover:text-white transition-all shadow-inner group"
              >
                <X size={24} className="group-hover:rotate-90 transition-transform duration-300" />
              </button>
            </div>

            {/* Modal Content - Scrollable */}
            <div className="flex-1 overflow-y-auto p-10 space-y-10 relative">
              {isDetailLoading && (
                <div className="absolute inset-0 bg-brand-card/50 backdrop-blur-sm z-20 flex items-center justify-center">
                  <Loader2 className="animate-spin text-brand-primary" size={48} />
                </div>
              )}

              {withdrawalDetail && (
                <div className="animate-in fade-in slide-in-from-bottom-4 duration-700 space-y-12">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">

                    {/* Entity Intelligence */}
                    <div className="space-y-6">
                      <h4 className="text-[10px] font-black text-brand-muted uppercase tracking-[0.3em] flex items-center gap-3">
                        <User size={16} className="text-brand-primary" />
                        Nexus Entity Information
                      </h4>
                      <div className="bg-brand-dark/30 border border-brand-border/40 p-8 rounded-3xl space-y-6 shadow-inner">
                        <AuditDetail icon={<User size={14} />} label="Market Identity" value={withdrawalDetail.userName} italic />
                        <AuditDetail icon={<FileText size={14} />} label="Verification Email" value={withdrawalDetail.userEmail} />
                        <AuditDetail icon={<ShieldCheck size={14} />} label="Protocol Identifier" value={withdrawalDetail.id} mono />
                      </div>

                      <div className="p-6 bg-brand-primary/5 border border-brand-primary/10 rounded-3xl flex items-start gap-5">
                        <div className="p-2 bg-brand-primary/10 rounded-xl">
                          <ShieldAlert size={22} className="text-brand-primary shrink-0" />
                        </div>
                        <div>
                          <p className="text-[11px] text-white font-black uppercase tracking-[0.2em] mb-1">Treasury Enforcement Alert</p>
                          <p className="text-[10px] text-brand-muted leading-relaxed uppercase font-bold tracking-widest opacity-80 italic">
                            Cross-reference amount with available wallet equity before authorization. Blockchain settlements represent permanent liquidity outflow.
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Financial Metadata */}
                    <div className="space-y-6">
                      <h4 className="text-[10px] font-black text-brand-muted uppercase tracking-[0.3em] flex items-center gap-3">
                        <Wallet size={16} className="text-brand-primary" />
                        Execution Metadata
                      </h4>
                      <div className="bg-brand-dark/30 border border-brand-border/40 p-8 rounded-3xl space-y-8 shadow-inner">
                        <div className="space-y-2">
                          <p className="text-[9px] text-brand-muted uppercase font-black tracking-[0.2em] opacity-60">Settlement Payout</p>
                          <div className="flex items-baseline gap-2">
                            <p className="text-4xl font-black text-white tracking-tighter font-mono">
                              ${parseFloat(withdrawalDetail.amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                            </p>
                            <span className="text-brand-primary font-black text-xs tracking-widest uppercase">{withdrawalDetail.asset}</span>
                          </div>
                        </div>

                        <div className="space-y-3 pt-6 border-t border-brand-border/20">
                          <p className="text-[9px] text-brand-muted uppercase font-black tracking-[0.2em] opacity-60">Verified Destination Address</p>
                          <div className="p-4 bg-black/40 rounded-2xl border border-brand-border/50 group relative overflow-hidden">
                            <div className="absolute top-0 right-0 w-12 h-12 bg-brand-primary/5 blur-xl group-hover:bg-brand-primary/10 transition-colors" />
                            <code className="text-xs font-mono text-brand-primary break-all relative z-10 block leading-relaxed">{withdrawalDetail.destinationAddress}</code>
                          </div>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-6">
                        <div className="bg-brand-dark/20 p-4 rounded-2xl border border-brand-border/30">
                          <AuditDetail label="Origin Sequence" value={new Date(withdrawalDetail.requestedAt).toLocaleString()} />
                        </div>
                        <div className="bg-brand-dark/20 p-4 rounded-2xl border border-brand-border/30">
                          <AuditDetail label="Settlement Node" value="Nexus-Treasury-8" />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Action Threshold */}
                  <div className="pt-12 border-t border-brand-border/30">
                    {withdrawalDetail.status === 'PENDING' ? (
                      <div className="space-y-8 max-w-2xl mx-auto">
                        {showRejectForm ? (
                          <div className="space-y-6 animate-in slide-in-from-top-4 duration-500 p-8 bg-red-500/5 border border-red-500/10 rounded-[32px]">
                            <div className="flex items-center gap-3 text-red-500 mb-2">
                              <ShieldAlert size={18} />
                              <p className="text-[10px] font-black uppercase tracking-[0.2em]">Compliance Violation Logic</p>
                            </div>
                            <Input
                              label="REJECTION REMARKS (PERMANENT SYSTEM LOG)"
                              placeholder="Enter security mismatch or AML discrepancy details..."
                              value={rejectionReason}
                              onChange={(e) => setRejectionReason(e.target.value)}
                              className="h-14 font-black text-xs"
                            />
                            <div className="flex gap-4">
                              <Button
                                onClick={() => handleAction('reject')}
                                variant="danger"
                                className="flex-1 h-16 text-[11px] font-black tracking-[0.2em] shadow-lg shadow-red-500/20"
                                isLoading={isProcessing}
                              >
                                PURGE & REJECT
                              </Button>
                              <Button
                                onClick={() => setShowRejectForm(false)}
                                variant="secondary"
                                className="px-10 h-16 text-[11px] font-black tracking-[0.2em]"
                              >
                                CANCEL
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <div className="flex flex-col sm:flex-row gap-6">
                            <Button
                              onClick={() => handleAction('approve')}
                              className="flex-1 h-16 text-xs font-black tracking-[0.3em] shadow-[0_10px_30px_rgba(var(--brand-primary-rgb),0.2)]"
                              isLoading={isProcessing}
                            >
                              <CheckCircle2 size={24} />
                              AUTHORIZE PAYOUT
                            </Button>
                            <Button
                              onClick={() => setShowRejectForm(true)}
                              variant="danger"
                              className="flex-1 h-16 text-xs font-black tracking-[0.3em] shadow-[0_10px_30px_rgba(239,68,68,0.2)]"
                            >
                              <XCircle size={24} />
                              DENY REQUEST
                            </Button>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className={`p-10 rounded-[40px] border ${statusStyles[withdrawalDetail.status]} text-center space-y-4 shadow-3xl animate-in zoom-in duration-500 max-w-2xl mx-auto relative overflow-hidden`}>
                        <div className="absolute top-0 right-0 p-6 opacity-10">
                          {withdrawalDetail.status === 'APPROVED' ? <CheckCircle2 size={100} /> : <XCircle size={100} />}
                        </div>
                        <p className="text-[11px] font-black uppercase tracking-[0.4em] opacity-80">Audit State: Immutable Process Complete</p>
                        <h4 className="text-3xl font-black tracking-tighter uppercase italic">{withdrawalDetail.status}</h4>

                        <div className="pt-6 mt-6 border-t border-current/10 space-y-4">
                          <div className="flex justify-center gap-8 text-[10px] font-black uppercase tracking-widest opacity-60">
                            <p>Reviewed By: {withdrawalDetail.reviewedBy || 'SYS-GATEWAY'}</p>
                            <p>Review Date: {withdrawalDetail.reviewedAt ? new Date(withdrawalDetail.reviewedAt).toLocaleString() : 'N/A'}</p>
                          </div>
                          {withdrawalDetail.remarks && (
                            <p className="text-sm font-bold leading-relaxed italic opacity-90 px-8">
                              "{withdrawalDetail.remarks}"
                            </p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="p-8 bg-brand-dark/60 border-t border-brand-border flex justify-end shrink-0">
              <Button onClick={() => setSelectedWithdrawalId(null)} variant="secondary" className="px-12 font-black uppercase tracking-[0.3em] text-[11px] h-12">
                CLOSE TERMINAL
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const AuditDetail: React.FC<{ icon?: React.ReactNode, label: string, value: string, italic?: boolean, mono?: boolean }> = ({ icon, label, value, italic, mono }) => (
  <div className="flex flex-col gap-1">
    <div className="flex items-center gap-2 opacity-60 group">
      {icon && <span className="text-brand-primary">{icon}</span>}
      <span className="text-[9px] text-brand-muted uppercase font-black tracking-[0.2em]">{label}</span>
    </div>
    <span className={`text-sm font-bold text-white tracking-tight ${italic ? 'italic' : ''} ${mono ? 'font-mono' : ''}`}>{value}</span>
  </div>
);

export default AdminWithdrawals;
